/**
 * Users & Roles. A thin client over /account/members + POST role:
 *   - The "Change role" control shows only with the `assign_role` capability.
 *   - The role picker is bounded by the server's `assignableRoles` (an admin can't
 *     pick "owner"); the server re-checks the lattice regardless.
 *   - Last-owner floor: downgrading the only owner is disabled client-side with an
 *     explanation, and still enforced server-side (409 last_owner).
 *   - Consequential elevation (→ admin/owner) shows a warning; electing a new owner
 *     (handing the billing root) additionally requires typing the member's email.
 *   - The server response is truth: on success we re-read the roster; on 403/409 we
 *     surface the server's reason in the dialog and roll nothing back.
 */

import { useState } from "react";
import { api, ApiError } from "../api/client";
import type { AccountContext, InviteLink, Member, PendingAssignment, Role } from "../api/types";
import { RoleChip, GuestChip } from "../components/Chips";
import { Modal } from "../components/Modal";
import { can, isConsequentialElevation, ROLE_LABEL } from "../lib/capabilities";
import { useToast } from "../components/Toast";
import { useAsyncData } from "../lib/useAsyncData";

export function UsersRoles({ ctx }: { ctx: AccountContext }) {
  const data = useAsyncData(() => api.members(), []);
  const [editing, setEditing] = useState<Member | null>(null);
  const canAssign = can(ctx.capabilities, "assign_role");

  return (
    <div>
      <h1>Users &amp; Roles</h1>
      <p className="screen-intro">
        Members appear here after their first sign-in. Roles are enforced by the server; you can
        assign up to your own level.
      </p>

      {canAssign && <InviteSection assignableRoles={ctx.assignableRoles} />}

      {data.loading && <p className="muted">Loading members…</p>}
      {data.error && (
        <div className="banner banner--error" role="alert">
          {data.error} <button className="btn--link" onClick={data.reload}>Retry</button>
        </div>
      )}

      {data.value && (
        <table className="table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data.value.members.map((m) => {
              const lastOwner = m.role === "owner" && data.value!.ownerCount <= 1;
              return (
                <tr key={m.sub}>
                  <td>
                    {m.email} {m.isSelf && <span className="muted">(you)</span>}
                  </td>
                  <td>
                    <RoleChip role={m.role} />
                  </td>
                  <td className="muted">{formatDate(m.joinedAt)}</td>
                  <td className="row-actions">
                    {canAssign && (
                      <button
                        className="btn btn--small"
                        onClick={() => setEditing(m)}
                        title={lastOwner ? "This is the only owner — assign another owner first." : undefined}
                      >
                        Change role
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {editing && data.value && (
        <ChangeRoleModal
          member={editing}
          assignableRoles={data.value.assignableRoles}
          ownerCount={data.value.ownerCount}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            data.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Invite / pre-assign (TRE-850, TRE-851). Lets an admin designate a role for an
 * email *before* that person has signed in; the server binds it on their first SSO
 * login (self-serve — no mail is sent). An outside-domain email is a cross-domain
 * guest invite: on first sign-in they join THIS account as a guest rather than
 * provisioning their own. Lists the currently-pending invites (guests badged) with
 * a revoke control. The role picker is bounded by the caller's `assignableRoles`;
 * the server re-checks the lattice regardless (thin-client §9).
 */
/**
 * Share an invite *link* (TRE-1044, Phase 0a). Mints a shareable, expiring URL
 * bound to a role; whoever opens it signs in and is bound to this account at that
 * role — no need to know their email ahead of time (the click-a-link half of
 * onboarding). The link's secret is shown exactly once, at creation; the active
 * links list carries only the non-secret id + role + expiry with a revoke control.
 * The role picker is bounded by `assignableRoles`; the server re-checks the lattice.
 */
function InviteLinkPanel({ assignableRoles }: { assignableRoles: Role[] }) {
  const data = useAsyncData(() => api.invites(), []);
  const toast = useToast();
  const [role, setRole] = useState<Role>(assignableRoles[0] ?? "creator");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The freshly-minted link, shown once. `url` falls back to building against this
  // window's origin when the server didn't return one (no admin web origin set).
  const [fresh, setFresh] = useState<{ url: string; role: Role } | null>(null);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.createInvite(role);
      const url = res.url ?? `${window.location.origin}/?invite=${encodeURIComponent(res.token)}`;
      setFresh({ url, role });
      data.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the invite link.");
    } finally {
      setBusy(false);
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast.push("Invite link copied to clipboard.");
    } catch {
      toast.push("Select the link and copy it manually.");
    }
  }

  async function revoke(inv: InviteLink) {
    try {
      await api.revokeInvite(inv.id);
      toast.push("Invite link revoked.");
      if (fresh && data.value?.invites.find((i) => i.id === inv.id)) setFresh(null);
      data.reload();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "Couldn't revoke the link.");
    }
  }

  return (
    <div className="invite-link-panel">
      <h2>Share an invite link</h2>
      <p className="muted">
        Generate a link that grants a role on sign-in — share it with anyone you want in this
        account. The link expires and is single-use; the secret is shown once, right after you
        create it.
      </p>
      <div className="invite-form">
        <label className="field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={busy}>
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn--primary" onClick={generate} disabled={busy}>
          {busy ? "Generating…" : "Generate link"}
        </button>
      </div>
      {error && (
        <div className="banner banner--error" role="alert">
          {error}
        </div>
      )}
      {fresh && (
        <div className="banner banner--info invite-link-fresh">
          <p>
            Share this {ROLE_LABEL[fresh.role]} link now — it won't be shown again:
          </p>
          <div className="row-actions">
            <input className="mono invite-link-url" readOnly value={fresh.url} onFocus={(e) => e.target.select()} />
            <button className="btn btn--small" onClick={() => copy(fresh.url)}>
              Copy
            </button>
          </div>
        </div>
      )}

      {data.value && data.value.invites.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Active invite link</th>
              <th>Role</th>
              <th>Expires</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data.value.invites.map((inv) => (
              <tr key={inv.id}>
                <td className="mono muted">…{inv.id.slice(-8)}</td>
                <td>
                  <RoleChip role={inv.role} />
                </td>
                <td className="muted">{formatDate(inv.expiresAt)}</td>
                <td className="row-actions">
                  <button className="btn btn--small" onClick={() => revoke(inv)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function InviteSection({ assignableRoles }: { assignableRoles: Role[] }) {
  const data = useAsyncData(() => api.pending(), []);
  const toast = useToast();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(assignableRoles[0] ?? "creator");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = email.trim().length > 0 && email.includes("@") && !busy;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.preAssign(email.trim(), role);
      toast.push(`${res.pending.email} will be ${ROLE_LABEL[role]} on first sign-in.`);
      setEmail("");
      data.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the invite.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(p: PendingAssignment) {
    try {
      await api.revokePending(p.email);
      toast.push(`Invite for ${p.email} revoked.`);
      data.reload();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "Couldn't revoke the invite.");
    }
  }

  return (
    <section className="invite-section">
      <InviteLinkPanel assignableRoles={assignableRoles} />

      <h2>Pre-assign by email</h2>
      <p className="muted">
        Prefer to bind a specific address? Give someone a role before they sign in and they pick it
        up automatically on their first sign-in with that email — no invite email is sent. An email
        outside your firm's domain joins as a guest of this account.
      </p>
      <div className="invite-form">
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@yourfirm.com"
            autoComplete="off"
            disabled={busy}
          />
        </label>
        <label className="field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={busy}>
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
          {busy ? "Saving…" : "Pre-assign"}
        </button>
      </div>
      {error && (
        <div className="banner banner--error" role="alert">
          {error}
        </div>
      )}

      {data.value && data.value.pending.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Pending invite</th>
              <th>Role</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {data.value.pending.map((p) => (
              <tr key={p.email}>
                <td>
                  {p.email}
                  {p.guest && <> <GuestChip /></>}
                </td>
                <td>
                  <RoleChip role={p.role} />
                </td>
                <td className="row-actions">
                  <button className="btn btn--small" onClick={() => revoke(p)}>
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function ChangeRoleModal(props: {
  member: Member;
  assignableRoles: Role[];
  ownerCount: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const { member, assignableRoles, ownerCount, onClose, onDone } = props;
  const toast = useToast();
  const [role, setRole] = useState<Role>(member.role);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = role !== member.role;
  const elevation = changed && isConsequentialElevation(member.role, role);
  const electingOwner = role === "owner" && member.role !== "owner";
  const removingLastOwner = member.role === "owner" && role !== "owner" && ownerCount <= 1;
  const phraseOk = !electingOwner || typed === member.email;
  const canSubmit = changed && !removingLastOwner && phraseOk && !busy;

  // The picker offers the caller's assignable set, plus the member's current role
  // so it's always visible as the selected option.
  const options = Array.from(new Set<Role>([member.role, ...assignableRoles]));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.assignRole(member.sub, role);
      toast.push(`${member.email} is now ${ROLE_LABEL[role]}.`);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't change the role.");
      setBusy(false);
    }
  }

  return (
    <Modal title={`Change role — ${member.email}`} onClose={busy ? () => {} : onClose}>
      <div className="modal-body">
        <label className="field">
          <span>Role</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} disabled={busy}>
            {options.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>

        {removingLastOwner && (
          <div className="banner banner--error" role="alert">
            This account must keep at least one owner. Promote another member to owner first.
          </div>
        )}
        {elevation && !removingLastOwner && (
          <div className="banner banner--info">
            {ROLE_LABEL[role]} is a governance role
            {role === "owner" ? " with billing control" : " that can manage trash and locks"}. Grant
            it only to people who should have it.
          </div>
        )}
        {electingOwner && (
          <label className="field">
            <span>
              Type <code className="mono">{member.email}</code> to confirm you're handing over an owner
              seat
            </span>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </label>
        )}
        {error && (
          <div className="banner banner--error" role="alert">
            {error}
          </div>
        )}
      </div>
      <div className="modal-actions">
        <button className="btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
          {busy ? "Saving…" : `Set ${ROLE_LABEL[role]}`}
        </button>
      </div>
    </Modal>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}
